import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { HiArrowLeft } from 'react-icons/hi';

const API =  process.env.REACT_APP_API_URL;

export default function OtpVerify() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || '';

  const [otp, setOtp] = useState(new Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expiryTimer, setExpiryTimer] = useState(600);    // OTP expires in 10 min
  const [resendTimer, setResendTimer] = useState(180);    // Resend available after 3 min

  const inputRefs = useRef([]);

  useEffect(() => {
    if (!email) navigate('/register');
    inputRefs.current[0]?.focus();
  }, [email, navigate]);

  // Separate countdown for OTP expiry
  useEffect(() => {
    if (expiryTimer <= 0) return;
    const interval = setInterval(() => setExpiryTimer(prev => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [expiryTimer]);

  // Separate countdown for resend cooldown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer(prev => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleChange = (index, value) => {
    if (value.length > 1) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').slice(0, 6);
    if (!/^\d+$/.test(paste)) return;
    const newOtp = [...otp];
    for (let i = 0; i < paste.length; i++) newOtp[i] = paste[i];
    setOtp(newOtp);
    inputRefs.current[Math.min(paste.length, 5)]?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) {
      setError('Please enter the full 6‑digit code');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/verify-otp`, { email, otp: code });
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      navigate('/');
    } catch (err) {
      setError(err.response?.data || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      // Call a new endpoint that resends the OTP for a pending registration
      await axios.post(`${API}/api/auth/resend-otp`, { email });
      setExpiryTimer(600);
      setResendTimer(180);
    } catch (err) {
      setError('Failed to resend code. Please try again.');
    }
  };

  // Can resend if the cooldown has passed OR the code has expired
  const canResend = resendTimer <= 0 || expiryTimer <= 0;
  // The code is still valid if expiryTimer > 0
  const codeExpired = expiryTimer <= 0;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Verify your email</h1>
          <p className="text-sm text-gray-500 mb-6">
            We sent a 6‑digit code to <span className="font-medium text-gray-700">{email}</span>
          </p>

          <form onSubmit={handleSubmit}>
            <div className="flex justify-between gap-2 mb-6" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-xl font-bold bg-gray-50 border border-gray-200 rounded-xl focus:border-gray-900 focus:ring-2 focus:ring-gray-900/5 outline-none transition"
                />
              ))}
            </div>

            {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

            <button
              type="submit"
              disabled={loading || otp.join('').length !== 6 || codeExpired}
              className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-semibold text-base hover:bg-black transition disabled:opacity-60"
            >
              {loading ? 'Verifying...' : codeExpired ? 'Code Expired' : 'Verify Email'}
            </button>
          </form>

          <div className="mt-5 text-center text-sm text-gray-500">
            {codeExpired ? (
              <span className="text-red-500">Code has expired.</span>
            ) : (
              <span>Code expires in {formatTime(expiryTimer)}</span>
            )}
          </div>

          <div className="mt-3 text-center">
            {canResend ? (
              <button
                onClick={handleResend}
                className="text-black font-medium underline"
                disabled={loading}
              >
                Resend code
              </button>
            ) : (
              <span>Resend available in {formatTime(resendTimer)}</span>
            )}
          </div>

          <div className="mt-3 text-center">
            <Link to="/login" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-black">
              <HiArrowLeft size={14} /> Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};